import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import moment from "moment";

import { get, isNil } from "lodash";

import {
  Grid,
  Card,
  Button,
  Divider,
  Checkbox,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { Alert } from "@mui/material";

import { getOrgs } from "redux/actions/Org";

import {
  formatProducts,
  getCurrentPlanAndInvoiceData,
  handleShouldUpdateLicense,
} from "./helpers";

import { StripeAPI } from "lib/api/StripeAPI";
import { HubspotAPI } from "lib/api/HubspotAPI";
import { CheckCircle, InfoRounded } from "@mui/icons-material";

import { isOrganizationChildOfNFR } from "utils";

import {
  freeProductPlacholder,
  productKeys,
  availablePlans,
} from "./constants";

import { Root, BillingDialog, rootClasses, dialogClasses } from "./styles.js";

const API = {
  stripe: new StripeAPI(),
  hubspot: new HubspotAPI(),
};

const FREE_LICENSE = "FREE";

export const baseContactSalesUrl = "https://info.blumira.com/pql-pricing";

const PlanOption = ({
  plan = {},
  selected,
  openCheckoutSession,
  isSuperadmin,
  handleStartTrial,
  isLoading = false,
  activePlan = "ADVANCED",
  isStripeCustomer = false,
}) => {
  const isActive = () => {
    if (isStripeCustomer) {
      if (!selected) return !plan.priceId;
      return plan.priceId === selected;
    } else {
      return plan.licenseValue === activePlan;
    }
  };

  if (isStripeCustomer) {
    return (
      <div
        className={rootClasses.planOption}
        style={isActive() ? { border: `2px solid #3E7EF8` } : {}}
        datacy={"billingPlanOption"}
      >
        {plan.billingPeriod && plan.amount ? (
          <div className={rootClasses.namePriceContainer}>
            <p className={rootClasses.billingPeriod}>
              Billed {plan.billingPeriod}
            </p>
            <p
              className={rootClasses.price}
            >{`$${plan.amount} per billable user`}</p>
          </div>
        ) : (
          <div className={rootClasses.namePriceContainer}>
            <p className={rootClasses.price}>{plan.description}</p>
          </div>
        )}
        <div className={rootClasses.listItems}>
          {isActive() && !isLoading ? (
            <div className={rootClasses.checkContainer}>
              <CheckCircle className={rootClasses.currentCheck} />
              <p>Current</p>
            </div>
          ) : (
            // disabled for 'direct', 'msp', 'reseller', and 'referral' segments
            (!plan.disableCreditCard || isSuperadmin) && (
              <p
                className={rootClasses.link}
                onClick={openCheckoutSession}
                datacy={"billingChangePlanBtn"}
              >
                {plan.productId ? "Change to this plan" : null}
              </p>
            )
          )}
        </div>
        {isSuperadmin && plan.amount && (
          <div>
            <Button
              onClick={handleStartTrial}
              color={"primary"}
              variant={"outlined"}
            >
              Start Trial
            </Button>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <Card
        className={rootClasses.newPlanContainer}
        datacy={"editionPackagingOption"}
      >
        <div className={rootClasses.newPlanPrimaryContent}>
          <p className={rootClasses.planNameText}>{plan.name}</p>
          {isActive() ? (
            <div className={rootClasses.checkContainer}>
              <CheckCircle className={rootClasses.currentCheck} />
              <p className={rootClasses.currentText}>Current plan</p>
            </div>
          ) : (
            <p className={rootClasses.descriptionText}>{plan.description}</p>
          )}
          <div>
            {plan.listItems?.map((planDetailText, i) => (
              <ul key={i} className={rootClasses.detailTextContainer}>
                <li className={rootClasses.detailText}>{planDetailText}</li>
              </ul>
            ))}
          </div>
        </div>
        {plan.bonusMaterial && (
          <div className={rootClasses.bonusMaterialContainer}>
            <p className={rootClasses.bonusMaterialTitle}>
              {plan.bonusMaterial.title || ""}
            </p>
            <p>{plan.bonusMaterial.description || ""}</p>
          </div>
        )}
      </Card>
    );
  }
};

const BillingPageView = (props) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [productQuantity, setProductQuantity] = useState(1);
  const [isStripeCustomer, setIsStripeCustomer] = useState(false);
  const [isUserOnLegacyPlan, setIsUserOnLegacyPlan] = useState(false);
  // const [promotionCode, setPromotionCode] = useState('') // exclude promo code for now
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentPlanData, setCurrentPlanData] = useState(null);
  const [numberOfTrialDays, setNumberOfTrialDays] = useState(0);
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [productQuantityMinimum, setProductQuantityMinimum] = useState(1);
  const [upgradeSubscriptionStep, setUpgradeSubscriptionStep] = useState(0);
  const [contactSalesUrl, setContactSalesUrl] = useState(baseContactSalesUrl);
  // eslint-disable-next-line no-unused-vars
  const [currentSelectedPricePlanId, setCurrentSelectedPricePlanId] =
    useState(null);
  const [isStripeQuantityDialogOpen, setIsStripeQuantityDialogOpen] =
    useState(false);
  const [isTrialDialogOpen, setIsTrialDialogOpen] = useState(false);
  const [hasOrgRequestedXdrTrial, setHasOrgRequestedXdrTrial] = useState(false);

  const getAndSetCustomerSubscription = async () => {
    setIsLoading(true);
    let activeCustomer = null;

    if (!props.currentOrg) return;

    // if this user is not a stripe customer yet
    // do not allow them to be and have them conact sales
    if (
      !props.currentOrg.stripeCustomerId ||
      props.currentOrg.license === FREE_LICENSE
    ) {
      setIsStripeCustomer(false);
      setProducts(availablePlans);
      setIsLoading(false);
      // do not continue with any Stripe related logic
      return;
    }

    if (props.location?.query?.session_id) {
      setIsStripeCustomer(true);
      // this user just finished a checkout session
      const checkoutSessionId = props.location.query.session_id;

      // get checkout session to find customer id
      const checkoutSessionResponse = await API.stripe.getCheckoutSession(
        checkoutSessionId
      );
      if (
        !!checkoutSessionResponse.success &&
        !!checkoutSessionResponse?.data?.session
      ) {
        const checkoutSession = checkoutSessionResponse.data.session;
        const {
          data: { customer },
        } = await API.stripe.getCustomer(checkoutSession.customer);
        activeCustomer = customer;
      }
    } else if (props?.currentOrg?.stripeCustomerId) {
      setIsStripeCustomer(true);
      // this user already has a customer id, use it
      const {
        data: { customer },
      } = await API.stripe.getCustomer(props.currentOrg.stripeCustomerId);
      activeCustomer = customer;
    }

    const {
      data: { prices },
    } = await API.stripe.getPrices();

    // gets current data and formats for UI
    // planData = { productId, renewalDate, invoiceEmail, subscriptionId }
    // invoiceData = { nextIssueDate, invoiceTotal }
    const { planData, invoiceData } =
      getCurrentPlanAndInvoiceData(activeCustomer);

    // determine whether or not current org requires a licensing change
    // this logic will likely be moved elsewhere in future iterations but
    // meets scope/requirements for v1 of self-service
    const licenseToUpdate = handleShouldUpdateLicense(
      prices,
      planData.productId,
      props.currentOrg
    );

    if (licenseToUpdate && invoiceData.isPaid) {
      props.currentOrg.set({
        license: licenseToUpdate,
        stripeCustomerId: activeCustomer.id,
      });
      props.currentOrg.update();
      setUpgradeSubscriptionStep(1);
      setIsUpgradeDialogOpen(true);
    } else if (
      licenseToUpdate === FREE_LICENSE &&
      props.currentOrg.license !== FREE_LICENSE
    ) {
      props.currentOrg.set({ license: licenseToUpdate });
      props.currentOrg.update();
    }

    let relevantProducts = formatProducts(
      prices,
      planData.priceId,
      props.currentOrg
    );

    setProducts(relevantProducts);
    setCurrentPlanData(planData);
    setCurrentInvoiceData(invoiceData);
    setIsLoading(false);
  };

  useEffect(() => {
    if (props.currentOrg) {
      const orgRequestedXdrTrialValue = localStorage.getItem(
        `hasOrgRequestedXdrTrial-${props.currentOrg.id}`
      );
      if (orgRequestedXdrTrialValue && orgRequestedXdrTrialValue === "true") {
        setHasOrgRequestedXdrTrial(true);
      }
    } else {
      setHasOrgRequestedXdrTrial(false);
    }
  }, [localStorage, props.currentOrg]);

  useEffect(() => {
    // get org's license
    const currentOrgLicense = get(
      props.currentOrg,
      ["config", "license"],
      "ADVANCED"
    );

    // check if current org's license is present in new product list
    const isCurrentLicenseLegacy = !products.find(
      ({ licenseValue }) => licenseValue === currentOrgLicense
    );

    // of not present, user should see warning of legacy plan
    setIsUserOnLegacyPlan(isCurrentLicenseLegacy);

    getAndSetCustomerSubscription();
  }, [props.currentOrg, products]);

  useEffect(() => {
    if (props.currentUser) {
      const { first_name = "", last_name = "", email = "" } = props.currentUser;
      const contactUrlToSet =
        baseContactSalesUrl +
        `?firstname=${first_name}&lastname=${last_name}&email=${email}`;
      setContactSalesUrl(contactUrlToSet);
    }
  }, [props.currentUser]);

  useEffect(() => {
    if (!props.currentOrg) {
      setIsLoading(true);
      props.getOrgs();
    }
  }, [props.currentOrg]);

  const handleChangePlan = async (selectedStripePriceId, isTrial = false) => {
    const newSelectedProduct = products.find(
      ({ priceId }) => priceId === selectedStripePriceId
    );
    setSelectedProduct(newSelectedProduct);

    if (isTrial) {
      // if this is a superadmin starting a trial for
      // a user, open dialog for trial day entry
      toggleStripeQuantityDialog();
    } else if (currentPlanData?.subscriptionId) {
      // if we have a subscription id already, open modal
      // to handle the subscription as stripe's checkout
      // session does not handle subscription updates
      handleSetProductQuantityMinimum(newSelectedProduct);
      toggleUpgradeDialog();
    } else {
      // otherwise we can open the stripe-hosted
      // checkout session to establish intent
      toggleStripeQuantityDialog();
    }
  };

  const handleStripeCheckoutSession = async () => {
    let currentCustomerId;

    if (props.currentOrg.stripeCustomerId) {
      currentCustomerId = props.currentOrg.stripeCustomerId;
    }

    const body = {
      customer: currentCustomerId || null,
      priceId: selectedProduct.priceId,
      redirectUrl: window.location.href,
      userCount: productQuantity,
      trialDays: numberOfTrialDays,
    };

    const {
      data: { session },
    } = await API.stripe.createCheckoutSession(body);

    // open stripe hosted checkout in same window
    window.location.href = session.url;
  };

  const handleCreateTrialSubscription = async () => {
    let currentCustomerId;

    if (props.currentOrg.stripeCustomerId)
      currentCustomerId = props.currentOrg.stripeCustomerId;

    const body = {
      trialDays: numberOfTrialDays,
      priceId: selectedProduct.priceId,
      customer: currentCustomerId || null,
    };

    const {
      data: { customerId },
    } = await API.stripe.createSubscription(body);

    props.currentOrg.set({
      stripeCustomerId: customerId,
      license: selectedProduct.license,
    });

    setIsLoading(true);
    setIsStripeQuantityDialogOpen(false);
    await props.currentOrg.update();
    setUpgradeSubscriptionStep(1);
    setIsUpgradeDialogOpen(true);
    setIsLoading(false);
  };

  const handleEditCustomer = async () => {
    // disable customer view if no customer to view
    if (!props.currentOrg.stripeCustomerId) return;

    const body = {
      redirectUrl: window.location.href,
      customer: props.currentOrg.stripeCustomerId,
    };

    const { data } = await API.stripe.createCustomerSession(body);

    // open stripe hosted customer portal
    window.location.href = data.session.url;
  };

  const handleUpgradeSubscription = async () => {
    // non-customers should never make it here
    // but better safe than sorry
    if (!props.currentOrg.stripeCustomerId) return;

    const body = {
      quantity: productQuantity,
      plan: selectedProduct.priceId,
      subscriptionId: currentPlanData.subscriptionId,
      // promotionCode: promotionCode, // exclude promo code for now
    };

    await API.stripe.updateSubscription(body);

    getAndSetCustomerSubscription();
    setUpgradeSubscriptionStep(1);
  };

  const handleUpgradeSubscriptionStepTwo = () => {
    // on reload, remove query string from href that
    // is provided by the stripe checkout session
    window.location = window.location.href.split("?")[0];
    toggleUpgradeDialog();
    setProductQuantity(productQuantityMinimum);
  };

  const toggleUpgradeDialog = () => {
    setIsChecked(false);
    setUpgradeSubscriptionStep(0);
    setIsUpgradeDialogOpen(!isUpgradeDialogOpen);
  };

  const toggleStripeQuantityDialog = (selectedStripePriceId) => {
    const stripePriceId = selectedStripePriceId ? selectedStripePriceId : null;
    setCurrentSelectedPricePlanId(stripePriceId);
    setIsStripeQuantityDialogOpen(!isStripeQuantityDialogOpen);
  };

  const toggleTrialDialog = () => {
    setIsTrialDialogOpen(!isTrialDialogOpen);
  };

  const toggleCheck = () => {
    setIsChecked(!isChecked);
  };

  const handleSetProductQuantityMinimum = (plan) => {
    // advanced licensing requires a
    // minimum of 50 purchased
    let minimum = plan.name.toLowerCase().includes("advanced") ? 50 : 1;

    setProductQuantity(minimum);
    setProductQuantityMinimum(minimum);
  };

  const handleProductQuantityChange = (e) => {
    setProductQuantity(e.target.value);
  };

  // exclude promo code for now
  // const handlePromotionCodeChange = e => {
  //   setPromotionCode(e.target.value)
  // }

  const handleNumberOfTrialDaysChange = (e) => {
    setNumberOfTrialDays(e.target.value);
  };

  const handleRequestXdrTrial = async () => {
    // create ticket in hubspot for sales
    API.hubspot.createTicket();

    // display dialog notifying user of ticket submissiong
    toggleTrialDialog();

    // save trial request in LS to avoid multiple tickets
    setHasOrgRequestedXdrTrial(true);
    localStorage.setItem(
      `hasOrgRequestedXdrTrial-${props.currentOrg.id}`,
      "true"
    );

    // ensure Zendesk component picks up on storage change
    window.dispatchEvent(new Event("storage"));
  };

  const shouldShowUpsell = () => {
    // exclude MSP orgs
    const isOrgAChildOrg = isOrganizationChildOfNFR(props.currentOrg);

    const isOrgAParentOrg =
      props.currentOrg &&
      props.currentOrg?.market === 20 &&
      isNil(props.currentOrg.parentId);

    return (
      !isOrgAChildOrg &&
      !isOrgAParentOrg &&
      props.currentOrg?.license === FREE_LICENSE
    );
  };

  return (
    <Root>
      <div className={rootClasses.page}>
        {currentPlanData && currentPlanData.isInTrial && (
          <Alert
            severity={"info"}
            action={
              <Button
                color={"inherit"}
                size={"small"}
                onClick={handleEditCustomer}
              >
                MANAGE SUBSCRIPTION
              </Button>
            }
          >
            <p className={rootClasses.alertText}>
              Your trial will end and you will be charged on{" "}
              {moment
                .unix(currentPlanData?.trialEndDate)
                .format("MMMM DD YYYY")}
            </p>
          </Alert>
        )}
        <div className={rootClasses.section}>
          {isLoading ? (
            <PlanOption
              isLoading={true}
              isStripeCustomer={false} // using new styling for larger font
              plan={{ description: "Loading your billing information..." }}
            />
          ) : (
            !!products.length && (
              <div>
                {shouldShowUpsell() && (
                  <div className={rootClasses.freeLicenceCtaContainer}>
                    <h1>
                      Increase Your Log Retention with a Free XDR Platform Trial
                    </h1>
                    <h2>
                      Meet industry standards today: Most compliance regulations
                      require <strong>at least one year</strong> of log
                      retention.
                    </h2>
                    <Button
                      disabled={hasOrgRequestedXdrTrial}
                      className={rootClasses.requestXdrTrialButton}
                      onClick={handleRequestXdrTrial}
                    >
                      {hasOrgRequestedXdrTrial
                        ? "Request Submitted"
                        : "Request Free 14 Day Trial"}
                    </Button>
                  </div>
                )}
                {isStripeCustomer ? (
                  <div className={rootClasses.productsContainer}>
                    <Grid direction={"row"} container spacing={2}>
                      <Grid
                        container
                        direction={"column"}
                        item
                        xs={3}
                        spacing={2}
                      >
                        <p className={rootClasses.productSectionTitle}>Free</p>
                        <Grid item style={{ height: "90%" }}>
                          <PlanOption
                            isStripeCustomer={true}
                            plan={freeProductPlacholder}
                            isSuperadmin={props.superadmin}
                            selected={
                              currentPlanData ? currentPlanData.priceId : null
                            }
                          />
                        </Grid>
                      </Grid>
                      {productKeys.map((productKey, idx) => (
                        <Grid
                          key={idx}
                          container
                          direction={"column"}
                          item
                          xs={3}
                          spacing={2}
                        >
                          <p className={rootClasses.productSectionTitle}>
                            {productKey}
                          </p>
                          {products
                            .filter(({ name }) => name.includes(productKey))
                            .map((plan, index) => (
                              <Grid item key={index}>
                                <PlanOption
                                  plan={plan}
                                  isStripeCustomer={true}
                                  isSuperadmin={props.superadmin}
                                  openCheckoutSession={() =>
                                    handleChangePlan(plan.priceId)
                                  }
                                  selected={
                                    currentPlanData
                                      ? currentPlanData.priceId
                                      : null
                                  }
                                  handleStartTrial={() =>
                                    handleChangePlan(plan.priceId, true)
                                  }
                                />
                              </Grid>
                            ))}
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                ) : (
                  <div>
                    <Grid container spacing={2}>
                      {products.map((plan, index) => (
                        <Grid xs={3} item key={index}>
                          <PlanOption
                            plan={plan}
                            isStripeCustomer={false}
                            activePlan={get(
                              props.currentOrg,
                              ["config", "license"],
                              "ADVANCED"
                            )}
                          />
                        </Grid>
                      ))}
                      <Grid
                        item
                        xs={12}
                        className={rootClasses.footerContainer}
                      >
                        {isUserOnLegacyPlan && (
                          <div className={rootClasses.infoContainer}>
                            <InfoRounded />
                            <p>
                              Your organization is currently on a Blumira Legacy
                              plan. To discuss moving to a new plan, please
                              contact sales
                            </p>
                          </div>
                        )}
                        <div className={rootClasses.ctaContainer}>
                          {props.license.isTagged("limited-to-xdr") ? (
                            <p>
                              Have questions or want to expand your agent count?
                            </p>
                          ) : (
                            <p>
                              Want to try XDR or SIEM + Endpoint Visibility?
                            </p>
                          )}
                          <Button
                            target={"_blank"}
                            color={"primary"}
                            variant={"contained"}
                            href={contactSalesUrl}
                            rel={"noopener noreferrer"}
                            className={rootClasses.ctaButton}
                          >
                            Contact sales
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                )}
                {isStripeCustomer && (
                  <p className={rootClasses.userHelperText}>
                    A "billable user" refers to the number of knowledge workers
                    at your organization; basically the number of employees with
                    emails who use them.
                  </p>
                )}
              </div>
            )
          )}
        </div>

        {isStripeCustomer && (
          <div className={rootClasses.section}>
            <p className={rootClasses.sectionTitle}>Renewal (monthly)</p>
            <div className={rootClasses.sectionInfoContainer}>
              <div
                className={`${rootClasses.sectionInfoCard} ${rootClasses.rightBorder}`}
              >
                <p className={rootClasses.sectionInfoCardTitle}>
                  Plan will renew on:
                </p>
                <p className={rootClasses.sectionInfoCardText}>
                  {currentPlanData?.renewalDate
                    ? moment
                        .unix(currentPlanData.renewalDate)
                        .format("MMMM DD YYYY")
                    : "no billable plan on file"}
                </p>
                {/* <p className={classes.link}>Try the Professional Plan risk-free for 30 days</p> TODO: tie to...something */}
                <div />
              </div>
              <div
                className={`${rootClasses.sectionInfoCard} ${rootClasses.rightBorder}`}
              >
                <p className={rootClasses.sectionInfoCardTitle}>
                  Billable users included:
                </p>
                <p className={rootClasses.sectionInfoCardText}>Unlimited</p>
                <div />
              </div>
              <div className={rootClasses.sectionInfoCard}>
                <p className={rootClasses.sectionInfoCardTitle}>
                  Send invoices to:
                </p>
                <p className={rootClasses.sectionInfoCardText}>
                  {currentPlanData?.invoiceEmail
                    ? currentPlanData.invoiceEmail
                    : "no billable plan on file"}
                </p>
                <p className={rootClasses.link} onClick={handleEditCustomer}>
                  Change contact
                </p>
              </div>
            </div>
          </div>
        )}

        {!!currentInvoiceData && !!isStripeCustomer && (
          <div className={rootClasses.section}>
            <div className={rootClasses.manageInvoicesContainer}>
              <p className={rootClasses.sectionTitle}>Invoices</p>
              <Button
                className={rootClasses.manageButton}
                onClick={handleEditCustomer}
                color={"primary"}
                variant={"outlined"}
              >
                Manage
              </Button>
            </div>
            <div className={rootClasses.sectionInfoContainer}>
              <div
                className={`${rootClasses.sectionInfoCard} ${rootClasses.rightBorder}`}
              >
                <p className={rootClasses.sectionInfoCardTitle}>
                  {" "}
                  Next invoice will be issued on:
                </p>
                <p className={rootClasses.sectionInfoCardText}>
                  {currentInvoiceData?.nextIssueDate
                    ? moment
                        .unix(currentInvoiceData.nextIssueDate)
                        .format("MMMM DD YYYY")
                    : "no invoice on file"}
                </p>
              </div>
              <div
                className={`${rootClasses.sectionInfoCard} ${rootClasses.rightBorder}`}
              >
                <p className={rootClasses.sectionInfoCardTitle}>
                  Billable users detected this invoice period:
                </p>
                <p className={rootClasses.sectionInfoCardText}>
                  {currentInvoiceData?.knowledgeWorkers
                    ? currentInvoiceData?.knowledgeWorkers
                    : "no invoice on file"}
                </p>
              </div>
              <div className={rootClasses.sectionInfoCard}>
                <p className={rootClasses.sectionInfoCardTitle}>
                  Invoice total:
                </p>
                <p className={rootClasses.sectionInfoCardText}>
                  {currentInvoiceData?.invoiceTotal
                    ? currentInvoiceData.invoiceTotal
                    : "no invoice on file"}
                </p>
              </div>
            </div>
          </div>
        )}

        <BillingDialog open={isUpgradeDialogOpen} onClose={toggleUpgradeDialog}>
          <DialogTitle>
            Upgrade{upgradeSubscriptionStep === 1 && "d"}{" "}
            {selectedProduct ? "to" : null} {selectedProduct?.name || ""}
          </DialogTitle>
          {upgradeSubscriptionStep === 0 ? (
            <DialogContent>
              <p>
                By checking the box and selecting to 'Upgrade Now' you are
                giving Blumira permission to upgrade your plan and incur any
                related charges to the credit card stored in Stripe.
              </p>
              <p>
                To view your saved credit card details, select 'Manage Credit
                Card' to open your Stripe customer portal.
              </p>
              <p>
                After upgrading you may need to{" "}
                <strong>
                  refresh your browser to experience relevant changes to your
                  account
                </strong>
                .
              </p>
              <TextField
                type={"number"}
                value={productQuantity}
                className={dialogClasses.productQuantity}
                label={"# of users"}
                onChange={handleProductQuantityChange}
                error={productQuantity < productQuantityMinimum}
                helperText={
                  productQuantity < productQuantityMinimum
                    ? `You must purchase at least ${productQuantityMinimum} license${
                        productQuantityMinimum > 1 ? "s" : ""
                      }`
                    : ""
                }
              />
              {/* 
              FYI: limiting promo code scope for now, leaving 
              code here for when we want to test upgrades 
            */}
              {/* <TextField
              type={'text'}
              value={promotionCode}
              label={'Promotion code'}
              className={classes.productQuantity}
              onChange={handlePromotionCodeChange}
            /> */}
              <div className={dialogClasses.certify}>
                <Checkbox
                  color={"primary"}
                  checked={isChecked}
                  onChange={toggleCheck}
                />
                <p>
                  I agree to upgrading my plan and to recieve a prorated invoice
                  immediately, charged to the credit card on file
                </p>
              </div>
            </DialogContent>
          ) : (
            <DialogContent>
              <p>Your plan was successfully upgraded.</p>
              <p>Please refresh your browser for your new experience.</p>
            </DialogContent>
          )}
          {upgradeSubscriptionStep === 0 ? (
            <DialogActions className={dialogClasses.dialogActions}>
              <Button
                disabled={!isChecked}
                onClick={handleUpgradeSubscription}
                color={"primary"}
                variant={"contained"}
              >
                Upgrade now
              </Button>
              <Button
                onClick={handleEditCustomer}
                color={"primary"}
                variant={"contained"}
              >
                Manage credit card
              </Button>
              <Button
                onClick={toggleUpgradeDialog}
                color={"primary"}
                variant={"outlined"}
              >
                Cancel
              </Button>
            </DialogActions>
          ) : (
            <DialogActions className={dialogClasses.dialogActions}>
              <Button
                onClick={handleUpgradeSubscriptionStepTwo}
                color={"primary"}
                variant={"contained"}
              >
                Refresh Browser
              </Button>
              <Button
                onClick={toggleUpgradeDialog}
                color={"primary"}
                variant={"outlined"}
              >
                Cancel
              </Button>
            </DialogActions>
          )}
        </BillingDialog>

        <BillingDialog
          open={isStripeQuantityDialogOpen}
          onClose={toggleStripeQuantityDialog}
          datacy={"billingChangePlanDialog"}
        >
          <DialogTitle>Change to {selectedProduct?.name || ""}</DialogTitle>
          <DialogContent>
            {props.superadmin && (
              <div>
                <p>Blumira super admins may begin trials for customers.</p>
                <div className={dialogClasses.stepContainer}>
                  <div className={dialogClasses.numberContainer}>
                    <p>1</p>
                  </div>
                  <p>Enter number of trial days below</p>
                </div>
                <div className={dialogClasses.stepContainer}>
                  <div className={dialogClasses.numberContainer}>
                    <p>2</p>
                  </div>
                  <p>Click 'Create Trial Subscription'</p>
                </div>
                <div className={dialogClasses.stepContainer}>
                  <div className={dialogClasses.numberContainer}>
                    <p>3</p>
                  </div>
                  <p>Refresh the browser to see your updates</p>
                </div>
                <div className={dialogClasses.stepContainer}>
                  <div className={dialogClasses.numberContainer}>
                    <p>4</p>
                  </div>
                  <p>
                    To cancel, select 'Manage Subscription' in the alert then in
                    the Stripe portal 'Cancel Plan'
                  </p>
                </div>
                <TextField
                  type={"number"}
                  value={numberOfTrialDays}
                  label={"# of trial days"}
                  onChange={handleNumberOfTrialDaysChange}
                  helperText={"free days added to beginning of subscription"}
                />
                <Divider className={dialogClasses.divider} />
              </div>
            )}
            <p>
              Blumira uses the secure Stripe Portal to manage subscriptions.
            </p>
            <div className={dialogClasses.stepContainer}>
              <div className={dialogClasses.numberContainer}>
                <p>1</p>
              </div>
              <p>Click Launch Stripe Portal below</p>
            </div>
            <div className={dialogClasses.stepContainer}>
              <div className={dialogClasses.numberContainer}>
                <p>2</p>
              </div>
              <p>
                Enter your total user count when prompted to 'Update Quantity'
              </p>
            </div>
            <div className={dialogClasses.stepContainer}>
              <div className={dialogClasses.numberContainer}>
                <p>3</p>
              </div>
              <p>Enter payment information, then click 'Subscribe'</p>
            </div>
          </DialogContent>
          <DialogActions className={dialogClasses.dialogActions}>
            {props.superadmin && (
              <Button
                onClick={handleCreateTrialSubscription}
                color={"primary"}
                variant={"contained"}
              >
                Create Trial Subscription
              </Button>
            )}
            <Button
              onClick={handleStripeCheckoutSession}
              color={"primary"}
              variant={"contained"}
            >
              Launch Stripe Portal
            </Button>
            <Button
              onClick={toggleStripeQuantityDialog}
              color={"primary"}
              variant={"outlined"}
            >
              Cancel
            </Button>
          </DialogActions>
        </BillingDialog>
        <BillingDialog open={isTrialDialogOpen} onClose={toggleTrialDialog}>
          <div className={dialogClasses.trialDialogContainer}>
            <p className={dialogClasses.trialDialogHeader}>
              Your Free XDR Platform Trial is Underway
            </p>
            <p className={dialogClasses.trialDialogText}>
              Our team will follow up with you shortly to activate a free 14 day
              trial of the full featured XDR Platform and will provide any
              support needed along the way.{" "}
            </p>
            <Button
              color={"primary"}
              variant={"outlined"}
              onClick={toggleTrialDialog}
              className={dialogClasses.trialDialogCloseButton}
            >
              Close
            </Button>
          </div>
        </BillingDialog>
      </div>
    </Root>
  );
};

const mapStateToProps = (state) => {
  const orgId = state.location.payload.orgId;

  const currentOrg = state.org.listOrgs.orgs.find((o) => o["id"] === orgId);

  return {
    orgId,
    currentOrg,
    license: state.license,
    location: state.location,
    currentUser: state?.session?.settings?.user,
    superadmin: state?.session?.settings?.user?.superadmin,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getOrgs: () => dispatch(getOrgs()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BillingPageView);
